// src/utils/session.js
//
// Single source of truth for the client-side session (access token, refresh
// token, user, last-activity timestamp) stored in localStorage. Shared by
// ApiClient/FileUploadApi (reactive 401 handling), the sign-in/sign-out
// pages, and useSessionWatcher (proactive idle-timeout + silent refresh).
import Swal from 'sweetalert2';

const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';
const LAST_ACTIVITY_KEY = 'last_activity_at';
const SESSION_CONFIG_KEY = 'session_config';
const SIGN_IN_PATH = '/authentication/sign-in';

// Used only until the first login/refresh response arrives (or if an older
// stored session predates this feature) — the backend's irm_session_config
// values (piggybacked on every login/refresh response) always take over
// once available, so a drift here never under-enforces the real timeout.
const DEFAULT_SESSION_CONFIG = {
  accessTokenExpiryMinutes: 15,
  idleTimeoutMinutes: 15,
  warningThresholdMinutes: 12,
  renewThresholdMinutes: 10,
  renewBufferSeconds: 30,
};

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

export const getUser = () => {
  try {
    return JSON.parse(localStorage.getItem(USER_KEY) || 'null');
  } catch {
    return null;
  }
};

export const getSessionConfig = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(SESSION_CONFIG_KEY) || 'null');
    if (!stored) return DEFAULT_SESSION_CONFIG;
    return { ...DEFAULT_SESSION_CONFIG, ...stored };
  } catch {
    return DEFAULT_SESSION_CONFIG;
  }
};

export const setSession = ({ token, refresh_token, user, session_config }) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  if (refresh_token) localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  if (session_config) localStorage.setItem(SESSION_CONFIG_KEY, JSON.stringify(session_config));
};

export const clearSession = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(LAST_ACTIVITY_KEY);
  localStorage.removeItem(SESSION_CONFIG_KEY);
};

// Written on user activity; read across tabs via the same localStorage key
// (no storage-event wiring needed — each tab's own timer just re-reads it).
export const recordActivity = () => {
  localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
};

export const getIdleMs = () => {
  const lastActivityAt = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
  if (!lastActivityAt) return 0;
  return Date.now() - lastActivityAt;
};

export const isIdleExceeded = (idleTimeoutMinutes) => {
  const idleTimeoutMs = idleTimeoutMinutes * 60 * 1000;
  return getIdleMs() >= idleTimeoutMs;
};

// Shared "session ended" UX for both the reactive 401 path (ApiClient/
// FileUploadApi) and the proactive idle-timer path (useSessionWatcher), so
// both render identical messaging instead of two separate copies.
export const redirectToSessionExpired = (message) => {
  clearSession();
  if (window.location.pathname === SIGN_IN_PATH) return;

  Swal.fire({
    icon: 'error',
    title: 'Session Expired',
    text: message || 'Your session has expired. Please login again.',
    confirmButtonText: 'OK',
    allowOutsideClick: false,
  }).then(() => {
    window.location.href = SIGN_IN_PATH;
  });
};
